import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { UkDateInput, ukValid } from "./UkDateInput";

const todayUk = () => new Date().toLocaleDateString("en-GB", { timeZone: "Europe/London" });

// Pre-publish impact review. Material change (fresh acknowledgement) is the default.
export function WsPublish({ rid, working, currentId, onClose, onDone }) {
  const [imp, setImp] = useState(null);
  const [fresh, setFresh] = useState(true);
  const [label, setLabel] = useState(working.revision || "");
  const [eff, setEff] = useState(todayUk());
  const [summary, setSummary] = useState(working.change_summary || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/policies/${rid}/publish-impact`).then((r) => setImp(r.data)).catch(() => setImp({ error: true })); }, [rid]);
  const go = async () => {
    if (busy) return; setBusy(true);
    try {
      if (working.status === "draft") await api.post(`/policies/revisions/${working.id}/submit`);
      if (working.status !== "approved") await api.post(`/policies/revisions/${working.id}/approve`);
      await api.post(`/policies/revisions/${working.id}/publish`, { revision: label, effective_date: eff, requires_fresh_ack: fresh,
        change_summary: summary, expected_current_revision_id: currentId || null });
      toast.success(`Rev ${label} published`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); } finally { setBusy(false); }
  };
  const ch = imp?.changes;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ws-publish" className="max-w-lg">
        <DialogHeader><DialogTitle>Publish revision</DialogTitle>
          <DialogDescription>Becomes the live version. The previous version and its signatures stay in history.</DialogDescription></DialogHeader>
        {!imp ? <p className="text-sm text-slate-400">Checking impact…</p> : imp.error ? <p className="text-sm text-red-600">Could not load the impact review.</p> : (
          <div data-testid="ws-impact" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm flex flex-col gap-1">
            <div><b>{imp.audience_count}</b> people in the audience{imp.current ? <> · <b>{imp.signed_current_count}</b> signed Rev {imp.current.revision}</> : " · first publication"}</div>
            {imp.is_pdf ? <div>{imp.pdf_replaced ? "PDF replaced with a new file." : "PDF unchanged."}</div>
              : ch && <div>{ch.changed} changed · {ch.added} added · {ch.removed} removed paragraphs</div>}
            <div className="text-slate-500">Booklets: {imp.booklets.length ? imp.booklets.join(", ") : "none"}</div>
            {!imp.ack_required && <div className="text-slate-500">This document does not require acknowledgement.</div>}
          </div>
        )}
        {imp?.current && (
          <div className="flex flex-col gap-1.5 text-sm">
            <label className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${fresh ? "border-emerald-400 bg-emerald-50" : "border-slate-200"}`}>
              <input type="radio" data-testid="ws-publish-fresh" checked={fresh} onChange={() => setFresh(true)} className="mt-1" />
              <span><b>Material change — everyone re-acknowledges</b><span className="block text-xs text-slate-500">{imp.if_fresh_ack} people will need to sign this revision.</span></span>
            </label>
            <label className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${!fresh ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}>
              <input type="radio" data-testid="ws-publish-minor" checked={!fresh} onChange={() => setFresh(false)} className="mt-1" />
              <span><b>Minor correction — signatures carry forward</b><span className="block text-xs text-slate-500">Only {imp.if_carry_forward} who haven't signed yet will be asked. Prior signatures stay linked to the revision they signed.</span></span>
            </label>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-slate-600">Revision label<Input data-testid="ws-publish-label" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1" /></label>
          <label className="text-xs font-semibold text-slate-600">Effective date<UkDateInput testid="ws-publish-effective" value={eff} onChange={setEff} /></label>
        </div>
        <Textarea data-testid="ws-publish-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What changed in this revision? (required)" className="min-h-[60px]" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ws-publish-confirm" disabled={busy || !imp || imp.error || !label.trim() || !summary.trim() || !eff || !ukValid(eff)} onClick={go} className="bg-emerald-700 hover:bg-emerald-800">{busy ? "Publishing…" : "Publish"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
